// core
import {Injectable} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';

// services
import {AuthDetailsService} from '../auth/auth-details.service';
import {TranslateService} from '@ngx-translate/core';

// models
import {ImageModel} from '../shared/models/image.model';
import {AuthDetails} from '../core/auth-details/auth-details.model';

// constants
import {AppConstants} from './constants';
import {forkJoin, Observable} from 'rxjs';


@Injectable()
export class UtilsService {

  constructor(
    private _authDetailsService: AuthDetailsService,
    private _translateService: TranslateService,
  ) {
  }

  public getPostalCodeRegex() {
    return /^[^\s].{1,}$/;
  }

  public getEmailRegexPattern() {
    return /^(([^<>()\[\]\\.,;:\s@äöüßÄÖÜẞ"]+(\.[^<>()\[\]\\.,;:\s@äöüßÄÖÜẞ"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([A-Za-z\-0-9]+\.)+[A-Za-z]{2,}))$/;
  }


  public getOnlyNumbersRegex() {
    return /^\d+$/;
  }


  public getPasswordRegex() {
    return /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/;
  }


  public getPhoneNumberRegex() {
    return /^(?:(?:\(?(?:00|\+)([1-4]\d\d|[1-9]\d?)\)?)?[\-\.\ \\\/]?)?((?:\(?\d{1,}\)?[\-\.\ \\\/]?){0,})(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i;
  }

  public resetDateFormControl(form: UntypedFormGroup, date: string) {
    form.controls[date].reset();
    form.controls[date].markAsTouched();
    form.controls[date].markAsDirty();
    return form;
  }

  public convertIsoDateStringToNgbDate(dateInMillis: any): { year: number, month: number, day: number } {
    let date: Date = null;
    if (dateInMillis) {
      date = new Date(dateInMillis);
      return {
        'year': date.getFullYear(),
        'month': date.getMonth() + 1,
        'day': date.getDate()
      }
    } else {
      return null;
    }
  }

  public convertNgbDateToIsoDateString(ngbDate: { year: number, month: number, day: number }): string {
    if (!ngbDate || !ngbDate.year || !ngbDate.month || !ngbDate.day) {
      return null;
    }

    return `${ngbDate.year}-${ngbDate.month.toString().padStart(2, '0')}-${ngbDate.day.toString().padStart(2, '0')}`;
  }

  public getImageUploadData(uploadedFile, imageSrc): ImageModel {
    const newUpload: ImageModel = new ImageModel();
    newUpload.content = imageSrc;
    newUpload.contentType = uploadedFile.type;
    newUpload.name = uploadedFile.name;
    newUpload.sizeInBytes = this.getSizeOfImageFromBase64(imageSrc);
    return newUpload;
  }

  public logout() {
    this._authDetailsService.getAuthDetails().subscribe((authDetails: AuthDetails) => {
      this.redirectTo('https://' + authDetails.domain + AppConstants.rootPath + '/oidc/logout');
    });
  }

  // the portal start page on the same domain requires a login of its own, so anonymous users are sent to Keycloak
  // from there; an existing session is kept
  public goToPortal() {
    this.redirectTo(window.location.origin + '/');
  }

  // wraps the window.location.href assignment, so it can be spied on in tests
  protected redirectTo(url: string) {
    window.location.href = url;
  }

  public concatFirstNameWithLastName(firstName?: string, lastName?: string) {
    return (firstName ? firstName : '') + (lastName ? ' ' + lastName : '');
  }

  // temporaryLanguage => is set after authentication and also used by other apps
  // lang => is set from login screen (no auth needed)
  public setDefaultLanguage(language?: string) {
    if (language) {
      localStorage.setItem('temporaryLanguage', language);
    } else {
      language = localStorage.getItem('temporaryLanguage') ?? localStorage.getItem('lang') ?? 'en';
    }

    this._translateService.setDefaultLang(language);
    this._translateService.use(language);
  }

  public initializeSelectOrMultiselect(data) {
    const list: Array<any> = new Array();
    let i = 0;

    if (data) {
      data.map((element) => {
        return this._translateService.get(element)
          .subscribe((translatedElement) => {
            list[i] = {
              value: element,
              label: translatedElement
            };
            i += 1;
          })
      });
    }
    // sorts elements of the list after translation
    list.sort((item_a, item_b) => item_a.label.localeCompare(item_b.label));
    return list;
  }

  public initializeListOfSalutations(data: string[]) {
    let listOfTitles = this.initializeSelectOrMultiselect(data);
    listOfTitles.forEach((title) => {
      if (AppConstants.disabledSalutationCodes.includes(title.value)) {
        title.disabled = true;
      }
    });
    listOfTitles.sort((title) => title.disabled);
    return listOfTitles;
  }

  /*
  * This is a better version from the one on top
  * TODO: after testing and proving this is a better version, a refactoring task is necessary to remove the old one
  * */
  public initializeSelectOrMultiselect2(data) {
    let list: Array<any> = [];
    let newObservable;

    if (data) {

      newObservable = new Observable(observer => {
        let translateObservables = data.map((element) => {
          return this._translateService.get(element)
        });

        forkJoin(translateObservables).subscribe((res: any[]) => {
          if (res) {
            list = res.map((item, index) => {
              return {
                value: data[index],
                label: item
              }
            });
            // Sort elements by label to match v1 behavior
            list.sort((item_a, item_b) => item_a.label.localeCompare(item_b.label));
          }
          observer.next(list);
          observer.complete();
        });

      });
    }

    return newObservable;
  }

  public getBoolVal(value) {
    let newBool = value;

    if (value == AppConstants.all) {
      newBool = null;
    }
    if (value == AppConstants.active || value == AppConstants.confirmed || value == AppConstants.yes) {
      newBool = true;
    }
    if (value == AppConstants.notActive || value == AppConstants.unconfirmed || value == AppConstants.no) {
      newBool = false;
    }

    return newBool;
  }

  private getSizeOfImageFromBase64(baseString) {
    // Split the base64 string in data and contentType
    const block = baseString.split(';');
    // get the real base64 content of the file (ex.: iVBORw0KGg....)
    const realData = block[1].split(',')[1];
    return atob(realData).length;
  }
}
